import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { BellRinging, BellSlash, ShieldWarning, CheckCircle } from "@phosphor-icons/react";
import { usePushNotifications } from "@/hooks/usePushNotifications";

/**
 * CoachNotificationSettings — browser-push subscribe toggle + per-category
 * notification switches for the coach. Subscribes the coach's browser/PWA
 * once, then lets them fine-tune which client events fire a push (messages,
 * workouts, streaks, weight drops, silent clients, low wellness, cancels).
 */
const CATEGORIES = [
  { key: "msg", label: "Client messages", desc: "When a client DMs you." },
  { key: "workout", label: "Workout logged", desc: "Every session a client saves." },
  { key: "streak", label: "Habit streak milestone", desc: "Client hits a 7 / 14 / 30-day streak." },
  { key: "weight", label: "Weight-drop milestone", desc: "Client drops −1 / −5 / −10 kg since starting." },
  { key: "low_wellness", label: "Low wellness flag", desc: "Client reports energy ≤ 2 or stress ≥ 4." },
  { key: "quiet", label: "Silent 3+ days", desc: "Client has no habits, logs, or messages for 3 days." },
  { key: "cancel", label: "Subscription cancelled", desc: "Client cancels — fires save-attempt window." },
  { key: "patterns", label: "Weekly pattern sweep", desc: "Sunday 6pm — repeating client miss patterns (e.g. 'misses water on Weds')." },
];

export default function CoachNotificationSettings() {
  const { supported, permission, subscribed, busy, subscribe, unsubscribe } = usePushNotifications();
  const [prefs, setPrefs] = useState(null);
  const [msg, setMsg] = useState("");
  const [savingKey, setSavingKey] = useState(null);
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [prefsRes, statusRes] = await Promise.all([
          http.get("/coach/notification-prefs"),
          http.get("/push/status"),
        ]);
        setPrefs(prefsRes.data);
        setStatus(statusRes.data);
      } catch (e) { setMsg(apiError(e, "Couldn't load notification prefs.")); }
    })();
  }, []);

  const sendTest = async () => {
    setTesting(true); setTestMsg("");
    try {
      await http.post("/push/test");
      setTestMsg("✓ Test push fired. Check your notifications tray now (may take 5-10s).");
      setTimeout(() => setTestMsg(""), 6000);
    } catch (e) {
      setTestMsg(apiError(e, "Couldn't send test push."));
    } finally { setTesting(false); }
  };

  const resetAllSubs = async () => {
    // Escape hatch: when subs are stuck failing but not returning 4xx (so the
    // server can't auto-clean them), nuke every sub for this user in one call.
    // The user then re-subscribes their current browser from scratch.
    if (!window.confirm(
      "Clear ALL registered devices?\n\n" +
      "This wipes every push subscription tied to your account (across every browser and device you've enabled). " +
      "After this, tap 'Turn on notifications' to re-register your current browser with a fresh key.\n\n" +
      "Use this if 'Send test push' keeps failing."
    )) return;
    setMsg("");
    setTestMsg("");
    try {
      const { data } = await http.delete("/push/subscriptions/mine");
      setStatus({ user_id: status?.user_id, active_subscriptions: 0, latest_registered_at: null, devices: [] });
      setMsg(`Cleared ${data.removed} stale subscription${data.removed === 1 ? "" : "s"}. Tap 'Turn on notifications' to re-enable this device.`);
      // If we're currently 'subscribed' locally, unsubscribe the browser-side
      // handle too so the hook state re-syncs with the empty DB
      if (subscribed) {
        try { await unsubscribe(); } catch { /* best-effort */ }
      }
    } catch (e) {
      setMsg(apiError(e, "Couldn't clear subscriptions."));
    }
  };

  const enable = async () => {
    setMsg("");
    const r = await subscribe();
    if (r?.ok) setMsg("Notifications ON — you'll get pinged for the categories you have enabled below.");
    else if (r?.reason === "denied") setMsg("Blocked at the OS level. See the fix below.");
    else if (r?.reason === "error" && r?.error) setMsg(`Couldn't enable: ${r.error}. On iPhone: install to Home Screen and open from that icon. Then fully close & reopen the app before retrying.`);
    else setMsg("Couldn't enable. On iPhone: install to Home Screen first, open from that icon, try again.");
  };

  const disable = async () => {
    if (!window.confirm("Turn all notifications OFF? You won't be pinged for anything.")) return;
    setMsg("");
    await unsubscribe();
    setMsg("Notifications OFF.");
  };

  const togglePref = async (key) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next); // optimistic
    setSavingKey(key);
    try {
      const { data } = await http.patch("/coach/notification-prefs", { [key]: next[key] });
      setPrefs(data);
    } catch (e) {
      setPrefs(prefs); // rollback
      setMsg(apiError(e, "Couldn't save that toggle."));
    } finally {
      setSavingKey(null);
    }
  };

  if (!supported) {
    return (
      <div className="id-card mb-6" data-testid="coach-notifications-unsupported">
        <div className="flex items-center gap-3">
          <BellSlash size={18} weight="bold" className="text-zinc-500" />
          <div>
            <div className="id-label text-zinc-500">Notifications</div>
            <p className="text-zinc-400 text-xs font-manrope mt-1">
              Your browser doesn&apos;t support push. On iPhone, add the app to your Home Screen (Share → Add to Home Screen) and open it from that icon.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const denied = permission === "denied";
  const on = subscribed && permission === "granted";

  return (
    <div
      className={
        "id-card mb-6 " +
        (on ? "border-[#FF3333]/40 bg-[#FF3333]/5" : denied ? "border-yellow-500/50 bg-yellow-500/5" : "border-[#262626]")
      }
      data-testid="coach-notifications-settings"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {on ? (
            <BellRinging size={22} weight="fill" className="text-[#FF3333]" />
          ) : denied ? (
            <ShieldWarning size={22} weight="fill" className="text-yellow-500" />
          ) : (
            <BellSlash size={22} weight="bold" className="text-zinc-500" />
          )}
          <div>
            <div className={"id-label " + (on ? "text-[#FF3333]" : denied ? "text-yellow-500" : "text-zinc-400")}>
              Coach notifications · {on ? "ON" : denied ? "BLOCKED" : "OFF"}
            </div>
            <p className="text-zinc-300 text-xs font-manrope mt-1 max-w-md">
              {on
                ? "You'll get pinged instantly for the categories toggled below. Fine-tune anytime."
                : denied
                ? "Notifications are blocked at the OS level. Fix in Settings → Notifications → Lazy Lean Method → Allow."
                : "Get pinged when clients message you, log workouts, hit milestones, or drop off. Turn it on once, tune the categories below."}
            </p>
          </div>
        </div>
        {!denied && (
          on ? (
            <button
              onClick={disable}
              disabled={busy}
              className="id-btn-ghost text-xs border-[#333] text-zinc-300 hover:border-white hover:text-white disabled:opacity-40 shrink-0"
              data-testid="coach-notif-disable-btn"
            >
              {busy ? "..." : "Turn all off"}
            </button>
          ) : (
            <button
              onClick={enable}
              disabled={busy}
              className="id-btn-primary text-xs disabled:opacity-40 shrink-0"
              data-testid="coach-notif-enable-btn"
            >
              <BellRinging size={14} weight="fill" />
              {busy ? "Enabling..." : "Turn on notifications"}
            </button>
          )
        )}
      </div>

      {msg && (
        <div className="mt-3 text-xs font-manrope text-zinc-300 border-l-2 border-[#FF3333] pl-3" data-testid="coach-notif-msg">
          {msg}
        </div>
      )}

      {/* Diagnostic — shows how many devices are subscribed + test-push button.
          Helps the coach self-diagnose "I toggled ON but nothing's arriving". */}
      {status && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#262626] pt-3" data-testid="coach-notif-diagnostic">
          <div className="text-[11px] font-manrope text-zinc-400">
            <span className={status.active_subscriptions > 0 ? "text-[#00CC66] font-bold" : "text-[#FF3333] font-bold"}>
              {status.active_subscriptions}
            </span>{" "}
            active device{status.active_subscriptions === 1 ? "" : "s"} subscribed
            {status.latest_registered_at && (
              <span className="text-zinc-600"> · newest {new Date(status.latest_registered_at).toLocaleDateString()}</span>
            )}
          </div>
          <button
            onClick={sendTest}
            disabled={testing || status.active_subscriptions === 0}
            className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#FF3333]/50 text-[#FF3333] hover:bg-[#FF3333]/10 px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="coach-notif-test-btn"
            title={status.active_subscriptions === 0 ? "Enable notifications first" : "Fire a test push to this device"}
          >
            {testing ? "Sending…" : "Send test push"}
          </button>
        </div>
      )}
      {testMsg && (
        <div className={"mt-2 text-xs font-manrope " + (testMsg.startsWith("✓") ? "text-[#00CC66]" : "text-[#FF3333]")} data-testid="coach-notif-test-msg">
          {testMsg}
          {!testMsg.startsWith("✓") && status?.active_subscriptions > 0 && (
            <button
              onClick={resetAllSubs}
              className="ml-2 underline text-[#FF3333] hover:text-white font-bold"
              data-testid="coach-notif-reset-subs-btn"
            >
              Clear all subscriptions
            </button>
          )}
        </div>
      )}

      {/* Per-category toggles — always rendered so the coach can pre-configure
          before subscribing the browser. Toggles auto-persist on click. */}
      {prefs && (
        <div className="mt-5 border-t border-[#262626] pt-4" data-testid="coach-notif-toggles">
          <div className="id-label text-zinc-500 mb-3">Ping me when…</div>
          <ul className="divide-y divide-[#262626]">
            {CATEGORIES.map((c) => {
              const isOn = !!prefs[c.key];
              const saving = savingKey === c.key;
              return (
                <li key={c.key} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white flex items-center gap-2">
                      {c.label}
                      {isOn && <CheckCircle size={13} weight="fill" className="text-[#00CC66]" />}
                    </div>
                    <div className="text-xs text-zinc-500 font-manrope">{c.desc}</div>
                  </div>
                  <button
                    role="switch"
                    aria-checked={isOn}
                    onClick={() => togglePref(c.key)}
                    disabled={saving}
                    className={
                      "relative w-11 h-6 rounded-full transition-colors shrink-0 " +
                      (isOn ? "bg-[#FF3333]" : "bg-[#262626]") +
                      (saving ? " opacity-50 cursor-wait" : " cursor-pointer")
                    }
                    data-testid={`coach-notif-toggle-${c.key}`}
                  >
                    <span
                      className={
                        "absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all " +
                        (isOn ? "left-[22px]" : "left-0.5")
                      }
                    />
                  </button>
                </li>
              );
            })}
          </ul>
          {!on && (
            <p className="mt-3 text-[11px] text-zinc-500 font-manrope">
              Toggles are saved instantly — but nothing pings you until you enable browser notifications above.
            </p>
          )}
        </div>
      )}

      {denied && (
        <div className="mt-4 text-xs font-manrope text-zinc-300 border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-1" data-testid="coach-notif-fix-ios">
          <div className="font-bold text-yellow-500 uppercase tracking-[0.15em] text-[10px]">iPhone fix</div>
          <div>1. Open <span className="text-white">iPhone Settings → Notifications</span></div>
          <div>2. Scroll to <span className="text-white">Lazy Lean Method</span> in the app list</div>
          <div>3. Toggle <span className="text-white">Allow Notifications</span> ON</div>
          <div>4. Come back here and refresh this page</div>
        </div>
      )}
    </div>
  );
}
